import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import Modal from "../../../components/modal/Modal";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import InlineLoading from "../../../components/loading/InlineLoading";
import StatusIndicator, {
  type StatusType,
} from "../../../components/status-indicator/StatusIndicator";
import DropdownMenu from "../../../components/dropdown-menu/DropdownMenu";
import MemberGroupList from "../../../components/member-list/MemberGroupList";
// @ts-ignore - JSX コンポーネント（型定義なし）。バナーと同じ IP 制限モーダルを再利用
import IpRestrictionModal from "../../../components/project-banner/components/IpRestrictionModal";
import {
  adminProjectsApi,
  useGetAdminProjectMembersQuery,
  type AdminProjectMemberSummary,
  type AdminProjectMembership,
  type AdminProjectRow,
} from "../../../app/services/adminProjects";
import {
  useUpdateMembershipMutation,
  useRemoveMembershipMutation,
} from "../../../app/accounts/memberships";
import { buildMembershipMenuItems } from "../../../utils/membershipMenuItems";

interface ProjectMembersModalProps {
  /** 一覧でメンバーシップセルを押した対象プロジェクト */
  project: AdminProjectRow;
  onClose: () => void;
  /**
   * メンバー一覧の再取得後に呼ばれる。親のテーブル行
   * （members / member_count）をローカル同期するために使う。
   */
  onMembersChanged?: (
    _projectId: string,
    _members: AdminProjectMemberSummary[],
  ) => void;
}

/** バナーのユーザーリストと同じ保存フィードバック（StatusIndicator）用 */
type ListStatus = {
  status: StatusType;
  message: string;
};

/**
 * プロジェクト管理表のメンバーシップセルから開くメンバー一覧モーダル。
 * `GET /api/admin/project-management/<id>/members` の結果を、プロジェクト
 * バナーのユーザーリストと同じグループ別表示（共通 `MemberGroupList`）で
 * 描画する。管理者視点のため SPECIALIST グループも表示する。
 *
 * 各行の 3 点リーダーからはバナーの責任者と同じ操作
 * （責任者権限の付与 / 解除・IP制限・退出・スペシャリストの招聘）ができる。
 * メニュー項目とガード条件は `buildMembershipMenuItems` で共通化している。
 */
const ProjectMembersModal: React.FC<ProjectMembersModalProps> = ({
  project,
  onClose,
  onMembersChanged,
}) => {
  const dispatch = useDispatch();
  const { data, isLoading, isError } = useGetAdminProjectMembersQuery(
    project.project_id,
  );

  const memberships = data?.data?.memberships ?? [];

  const [updateMembership] = useUpdateMembershipMutation();
  const [removeMembership] = useRemoveMembershipMutation();

  const [ipRestrictionTarget, setIpRestrictionTarget] =
    useState<AdminProjectMembership | null>(null);
  const [removeTarget, setRemoveTarget] =
    useState<AdminProjectMembership | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const [listStatus, setListStatus] = useState<ListStatus>({
    status: "idle",
    message: "",
  });
  const listStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showListStatus = (status: ListStatus["status"], message: string) => {
    if (listStatusTimer.current) clearTimeout(listStatusTimer.current);
    setListStatus({ status, message });
    if (status === "saved") {
      listStatusTimer.current = setTimeout(() => {
        setListStatus({ status: "idle", message: "" });
      }, 2000);
    }
  };
  useEffect(() => {
    return () => {
      if (listStatusTimer.current) clearTimeout(listStatusTimer.current);
    };
  }, []);

  // 一覧が更新されたら親のテーブル行（アバタースタック・人数）も同期する
  useEffect(() => {
    if (!data?.data || !onMembersChanged) return;
    onMembersChanged(
      project.project_id,
      data.data.memberships.map((m) => ({
        user_id: m.user_id,
        position: m.position,
        display_name_jp: m.display_name_jp,
        display_name_en: m.display_name_en,
        avatar_url: m.avatar_url,
        order_user: m.order_user,
        is_invited: m.is_invited,
      })),
    );
  }, [data, onMembersChanged, project.project_id]);

  /** 操作成功後にモーダル内の一覧を再取得する */
  const refreshMembers = useCallback(() => {
    dispatch(
      adminProjectsApi.util.invalidateTags([
        { type: "AdminProjectMembers", id: project.project_id },
      ]),
    );
  }, [dispatch, project.project_id]);

  // ===== メンバーシップ操作ハンドラ =====

  const handleUpdatePosition = async (
    member: AdminProjectMembership,
    newPosition: string,
  ) => {
    showListStatus("saving", "更新中...");
    try {
      await updateMembership({
        projectId: project.project_id,
        userId: member.user_id,
        position: newPosition,
      }).unwrap();
      refreshMembers();
      showListStatus("saved", "更新しました");
    } catch (err) {
      showListStatus("error", "更新に失敗しました");
      console.error("ポジション更新に失敗:", err);
    }
  };

  const confirmRemoveMember = async () => {
    if (!removeTarget) return;
    setIsRemoving(true);
    showListStatus("deleting", "");
    try {
      await removeMembership({
        projectId: project.project_id,
        userId: removeTarget.user_id,
      }).unwrap();
      refreshMembers();
      showListStatus("saved", "退出させました");
      setRemoveTarget(null);
    } catch (err) {
      showListStatus("error", "退出に失敗しました");
      console.error("メンバーシップ削除に失敗:", err);
    } finally {
      setIsRemoving(false);
    }
  };

  // ===== 行メニュー =====

  const primaryClientCount = memberships.filter(
    (m) => m.position === "CLIENT" && !m.is_invited,
  ).length;
  const primaryProducerCount = memberships.filter(
    (m) => m.position === "PRODUCER" && !m.is_invited,
  ).length;

  const renderActions = (member: AdminProjectMembership) => {
    // 管理者は全グループを操作できる（バナーのプロデューサー責任者と同じメニュー）
    const items = buildMembershipMenuItems(member, {
      canControlClients: true,
      canControlProducers: true,
      primaryClientCount,
      primaryProducerCount,
      onUpdatePosition: handleUpdatePosition,
      onOpenIpRestriction: setIpRestrictionTarget,
      onRemove: setRemoveTarget,
    });
    return items.length > 0 ? <DropdownMenu items={items} /> : undefined;
  };

  const removeTargetName =
    removeTarget?.display_name_jp || removeTarget?.display_name_en || "";

  return (
    <Modal
      title="メンバーシップ"
      titleSize="medium"
      description={`${project.code_name || project.name}（${
        data?.data?.count ?? project.member_count
      }人）`}
      descriptionTone="subtle"
      onClose={onClose}
    >
      {isLoading && <InlineLoading visible />}
      {isError && (
        <p className="bodytext-13 u-text-light-gray">
          メンバー一覧の取得に失敗しました。
        </p>
      )}
      {!isLoading && !isError && memberships.length === 0 && (
        <p className="bodytext-13 u-text-light-gray">メンバーがいません。</p>
      )}
      {memberships.length > 0 && (
        <MemberGroupList members={memberships} renderActions={renderActions} />
      )}

      <StatusIndicator status={listStatus.status} inModal>
        {listStatus.message}
      </StatusIndicator>

      {/* IP制限モーダル（バナーと共通） */}
      {ipRestrictionTarget && (
        <IpRestrictionModal
          membership={ipRestrictionTarget}
          projectId={project.project_id}
          onClose={() => setIpRestrictionTarget(null)}
        />
      )}

      {/* 退出確認モーダル */}
      {removeTarget && (
        <ConfirmModal
          message={`${removeTargetName} さんをこのプロジェクトから退出させますか？`}
          confirmLabel="退出"
          loading={isRemoving}
          onConfirm={confirmRemoveMember}
          onCancel={() => setRemoveTarget(null)}
        />
      )}
    </Modal>
  );
};

export default ProjectMembersModal;
